/**
 * ChangePasswordPage — para mustChangePassword=true tras primer login y
 * también accesible voluntariamente.
 *
 * UX:
 *  - Mostramos los requisitos de política SIEMPRE (no sólo como error) con
 *    estado visual por requisito (cumplido / pendiente) — WCAG 3.3.2.
 *  - Validamos en cliente para feedback rápido, pero el backend es la fuente
 *    de verdad.
 *  - Sin sesión (acceso directo a la URL con cookie muerta), redirigimos a
 *    /login — probe async vía fetchMe (Sprint 16: no hay check síncrono).
 *  - El error 401 con currentPassword incorrecta NO debe llevarnos a /login
 *    (estamos ya autenticados) — por eso usamos fetch directo (con
 *    credentials:'include', la cookie hace la auth) en lugar del helper
 *    `api()` que redirige. Sprint 19 (F7): el OTRO 401 (error !=
 *    'invalid_credentials', o sea la sesión caducó mientras se rellenaba el
 *    form) sí redirige a /login?expired=1 con el aviso digno de F5.
 *  - Sprint 16: al cambiar la password el backend revoca TODAS las demás
 *    sesiones del usuario; la actual sigue viva.
 */
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { KeyRound, AlertCircle, Check, Circle } from 'lucide-react'
import { fetchMe, getCurrentUser, setCurrentUser } from '@/lib/auth'

interface Result {
  ok?: boolean
  error?: string
  message?: string
  reasons?: string[]
}

interface PolicyRule {
  id: string
  label: string
  test: (p: string) => boolean
}

const POLICY_RULES: PolicyRule[] = [
  { id: 'len', label: 'Mínimo 12 caracteres', test: (p) => p.length >= 12 },
  { id: 'lower', label: 'Al menos una minúscula', test: (p) => /[a-z]/.test(p) },
  { id: 'upper', label: 'Al menos una mayúscula', test: (p) => /[A-Z]/.test(p) },
  { id: 'digit', label: 'Al menos un dígito o símbolo', test: (p) => /[0-9\W_]/.test(p) },
]

function clientPolicy(p: string): string[] {
  return POLICY_RULES.filter((r) => !r.test(p)).map((r) => r.label)
}

export function ChangePasswordPage() {
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [done, setDone] = useState(false)
  const [attempted, setAttempted] = useState(false)
  const navigate = useNavigate()

  // Sin sesión (acceso directo a la URL) → /login. Si ya hay user en memoria
  // (venimos de login o del AuthGuard) nos ahorramos el round-trip.
  useEffect(() => {
    if (getCurrentUser()) return
    let alive = true
    void fetchMe()
      .then((user) => {
        if (alive && !user) navigate('/login', { replace: true })
      })
      .catch(() => {
        // Red/5xx (contrato F6 de fetchMe): no expulsamos — el backend es la
        // fuente de verdad cuando se envíe el formulario.
      })
    return () => {
      alive = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const clientIssues = clientPolicy(newPassword)
  const mismatch = newPassword !== '' && confirm !== '' && newPassword !== confirm

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setAttempted(true)
    if (mismatch || clientIssues.length > 0) return
    setBusy(true)
    setErr(null)
    try {
      const BASE = (import.meta.env.VITE_API_URL as string | undefined) ?? '/api'
      const res = await fetch(`${BASE}/v1/marketing/auth/change-password`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ currentPassword, newPassword }),
      })
      const data = (await res.json().catch(() => ({}))) as Result
      if (!res.ok) {
        // 401 con error:'invalid_credentials' = password ACTUAL incorrecta
        // (seguimos autenticados: se muestra en el form). Cualquier otro 401
        // viene del preHandler authenticate = la SESIÓN caducó mientras
        // rellenábamos el form (Sprint 19 — F7): a /login con el aviso digno
        // de F5 y deep-link de vuelta aquí, no un «Error.» genérico.
        if (res.status === 401 && data.error !== 'invalid_credentials') {
          setCurrentUser(null)
          navigate(`/login?expired=1&next=${encodeURIComponent('/change-password')}`, {
            replace: true,
          })
          return
        }
        const reasons = data.reasons?.length ? ` (${data.reasons.join(', ')})` : ''
        const base = data.message ?? 'no se pudo cambiar la contraseña'
        setErr(`${base.charAt(0).toUpperCase() + base.slice(1)}${reasons}.`)
        return
      }
      // Actualiza cache user con mustChangePassword=false.
      const cur = getCurrentUser()
      if (cur) setCurrentUser({ ...cur, mustChangePassword: false })
      setDone(true)
      // Subimos el timeout a 1500ms para que screen readers anuncien el mensaje
      // de éxito antes de la navegación (WCAG 2.2.1 — confirm message audible).
      setTimeout(() => navigate('/marketing/dashboard', { replace: true }), 1500)
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="min-h-screen bp-grid flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-6">
          <div className="text-[10px] font-mono uppercase tracking-wider text-blueprint-700">
            JR2 MKT · Admin
          </div>
          <h1 className="text-ink-900 font-semibold text-lg mt-1">Cambiar contraseña</h1>
        </div>
        <form onSubmit={submit} className="bp-card p-5 space-y-4" noValidate>
          <div>
            <label
              htmlFor="cp-current"
              className="block text-xs font-mono uppercase tracking-wider text-ink-700/70 mb-1"
            >
              Contraseña actual
            </label>
            <input
              id="cp-current"
              name="currentPassword"
              type="password"
              autoComplete="current-password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              required
              aria-invalid={Boolean(err)}
              aria-describedby={err ? 'cp-error' : undefined}
              className="w-full border border-ink-900/15 rounded px-3 py-2 text-sm bg-white"
            />
          </div>
          <div>
            <label
              htmlFor="cp-new"
              className="block text-xs font-mono uppercase tracking-wider text-ink-700/70 mb-1"
            >
              Nueva contraseña
            </label>
            <input
              id="cp-new"
              name="newPassword"
              type="password"
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              aria-invalid={attempted && clientIssues.length > 0}
              aria-describedby="cp-policy"
              className="w-full border border-ink-900/15 rounded px-3 py-2 text-sm bg-white"
            />
            {/* Política SIEMPRE visible (WCAG 3.3.2 Labels or Instructions).
                Cada regla muestra check verde si se cumple, círculo gris si
                pendiente. Color rojo SOLO tras intento submit fallido. */}
            <ul
              id="cp-policy"
              aria-live="polite"
              className="mt-2 text-[10px] font-mono space-y-0.5"
            >
              {POLICY_RULES.map((r) => {
                const ok = r.test(newPassword)
                const failedAfterSubmit = attempted && !ok
                const colorClass = ok
                  ? 'text-emerald-700'
                  : failedAfterSubmit
                    ? 'text-red-700'
                    : 'text-ink-700/60'
                return (
                  <li key={r.id} className={`flex items-center gap-1.5 ${colorClass}`}>
                    {ok ? (
                      <Check className="size-3 shrink-0" aria-hidden="true" />
                    ) : (
                      <Circle className="size-3 shrink-0" aria-hidden="true" />
                    )}
                    <span>{r.label}</span>
                  </li>
                )
              })}
            </ul>
          </div>
          <div>
            <label
              htmlFor="cp-confirm"
              className="block text-xs font-mono uppercase tracking-wider text-ink-700/70 mb-1"
            >
              Confirmar nueva contraseña
            </label>
            <input
              id="cp-confirm"
              name="confirmPassword"
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              aria-invalid={mismatch}
              aria-describedby={mismatch ? 'cp-mismatch' : undefined}
              className="w-full border border-ink-900/15 rounded px-3 py-2 text-sm bg-white"
            />
            {mismatch && (
              <div
                id="cp-mismatch"
                aria-live="polite"
                className="mt-1 text-[10px] font-mono text-red-700"
              >
                No coinciden.
              </div>
            )}
          </div>

          {err && (
            <div
              id="cp-error"
              role="alert"
              aria-live="assertive"
              className="flex items-start gap-2 text-xs font-mono text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1.5"
            >
              <AlertCircle className="size-3.5 mt-0.5 shrink-0" aria-hidden="true" />
              <span>{err}</span>
            </div>
          )}
          {done && (
            <div
              role="status"
              aria-live="polite"
              className="flex items-start gap-2 text-xs font-mono text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-1.5"
            >
              <Check className="size-3.5 mt-0.5 shrink-0" aria-hidden="true" />
              <span>Contraseña cambiada, redirigiendo…</span>
            </div>
          )}

          <button
            type="submit"
            disabled={busy || !currentPassword || !newPassword || mismatch || clientIssues.length > 0}
            aria-busy={busy}
            className="bp-btn-primary w-full justify-center"
          >
            <KeyRound className="size-3.5" aria-hidden="true" />
            {busy ? 'Guardando…' : 'Cambiar contraseña'}
          </button>
        </form>
      </div>
    </div>
  )
}

export default ChangePasswordPage
